/*eslint-disable*/
import React, { useMemo, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import UploadSiteFiles from 'components/SitesUploads'
import Uploader from '../../../About/uploader'
import { requestUpdateCriterionEFiles, requestDeleteCriterionEFiles, requestGetCriterionEFiles } from 'api/criterionE'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { ProfileContext } from 'providers/profile'
import { canViewSensitive } from 'utils/rolePermissions'

/**
 * CriterionEDocsUploader Component
 * 
 * Behavior Requirements:
 * 1. Visible during all phases of proposal (draft to confirmation)
 * 2. Following confirmation, reports continue to be visible alongside the assessment
 * 3. On reassessment:
 *    - Reports should be visible (original report if unchanged, or new report if replaced)
 *    - Proposer can propose with unchanged information (original report still applicable)
 *    - Proposer can delete it (if removing Criterion E)
 *    - Proposer can replace it with an updated report (if reassessing Criterion E)
 * 
 * Implementation:
 * - Files are always visible when criterionEs exist (via allowDownloadsWhenConfirmed={true})
 * - Upload/delete are disabled when site is confirmed or not in draft/edit-only status
 * - During reassessment, siteHistoryId is used to fetch files for that specific reassessment
 *   (backend should return original assessment files if no new files uploaded)
 */
const CriterionEDocsUploader = ({ site, criterionEs = [], criterionE = null }) => {
  const params = useParams()
  const { profile } = useContext(ProfileContext) || {}

  const siteId = site?.id || params.id
  const siteHistoryId = params.siteHistoryId || site?.sitehistoryId || null
  const isReassessment = Boolean(siteHistoryId)
  const criterionEsCount = criterionEs?.length ?? 0
  const hasCriterionEs = criterionEsCount > 0

  // User role checks
  const isKbaSecOrRfp = canViewSensitive(profile?.RoleId)

  const uploaderParams = useMemo(() => {
    return {
      id: siteId,
      siteHistoryId
    }
  }, [siteId, siteHistoryId])

  // For Criterion E, files should be visible during all phases including confirmation
  // But upload/delete should be disabled when confirmed or not in draft/edit-only status
  const isConfirmedOrLater = [SITE_STATUS_CONFIRMED_ID, SITE_STATUS_SUPERSEDED_ID].includes(site.SiteStatusId)
  
  // Check if at least one criterionE has been created
  // This can be either:
  // 1. There are criterionEs in the array, OR
  // 2. The individual criterionE item exists and has been created (has an ID)
  const hasCreatedCriterionE = hasCriterionEs || (criterionE && (criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id))
  
  // Render in this assessment flow only if:
  // - We have a siteId
  // - At least one Criterion E exists
  // - Site is not yet confirmed or superseded
  if (!siteId || !hasCreatedCriterionE || isConfirmedOrLater) return null

  // Upload/delete should be disabled when:
  // 1. Site is confirmed or superseded (read-only - files visible but not editable)
  // 2. Site is not in draft or edit-only status (no editing allowed)
  // During reassessment (when siteHistoryId exists), proposer can:
  // - View original files (if proposing with unchanged information)
  // - Delete files (if removing Criterion E)
  // - Replace files (if reassessing Criterion E)
  const editableStatuses = [SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID]
  const disabled = !editableStatuses.includes(site.SiteStatusId) || isConfirmedOrLater

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <UploadSiteFiles
          label="uploadCriterionEReport"
          field="uploadCriterionEReport"
          infoTooltip="criterionEUploaderHint"
          height="128px"
          dataTest="upload-criterion-e-docs"
          isOptional={true}
          disabled={disabled}
        />
      </div>
      <div style={{ padding: '0', marginTop: '8px' }}>
        <Uploader
          key={`${uploaderParams.id || 'site'}-${uploaderParams.siteHistoryId || 'assessment'}-criterion-e-${criterionEsCount}`}
          params={uploaderParams}
          siteFiles={[]}
          disabled={disabled}
          uploadFn={requestUpdateCriterionEFiles}
          deleteFn={requestDeleteCriterionEFiles}
          fetchFilesFn={async (fetchSiteId, fetchSiteHistoryId) => {
            try {
              // For proposers/NCG during reassessment: blank slate (do not show previous assessment files)
              if (isReassessment && !isKbaSecOrRfp) {
                return { files: [] }
              }

              // Ensure siteId is a primitive value
              const actualSiteId =
                typeof fetchSiteId === 'object' ? (fetchSiteId?.id || fetchSiteId?.siteId) : fetchSiteId
              const actualHistoryId =
                typeof fetchSiteHistoryId === 'object'
                  ? (fetchSiteHistoryId?.siteHistoryId || fetchSiteHistoryId?.historyId)
                  : fetchSiteHistoryId

              const res = await requestGetCriterionEFiles(actualSiteId, actualHistoryId)
              return res
            } catch (error) {
              console.error('Failed to fetch Criterion E files:', error)
              return { files: [] }
            }
          }}
          fileType="criterion-e"
          allowDownloadsWhenConfirmed={true}
        />
      </div>
    </div>
  )
}

export default CriterionEDocsUploader

